import { useEffect, useMemo, useState } from "react"

import { useAdminUsers } from "@/api/hooks/admin"
import {
  useCreateKlowPixel,
  useDeleteKlowPixel,
  useKlowPixelDetails,
  useKlowPixels,
  useUpdateKlowPixel,
  type KlowPixel,
} from "@/api/hooks/klow"
import { useAuth } from "@/auth/AuthProvider"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { IntuitiveMultiSelect } from "@/pages/form/widgets/IntuitiveMultiSelect"

const DEFAULT_S2S_EVENTS = {
  lander: "PageView",
  adclick: "Lead",
  serp: "Search",
}

type EditSnapshot = {
  name: string
  type: string
  userIds: string[]
  pixelCode: string
  events: Record<string, string>
}

const S2S_EVENT_SUGGESTIONS = [
  "PageView",
  "ViewContent",
  "ClickButton",
  "Search",
  "Lead",
  "Purchase",
  "AddToCart",
  "CompleteRegistration",
  "InitiateCheckout",
  "SubmitForm",
  "Contact",
  "Download",
]

function showToast(msg: string, type: "success" | "error" = "success") {
  const el = document.createElement("div")
  el.textContent = msg
  el.className = `fixed top-4 right-4 z-[9999] rounded-lg px-4 py-3 text-sm font-medium shadow-lg ${
    type === "error"
      ? "bg-red-900/90 text-red-200 border border-red-700"
      : "bg-green-900/90 text-green-200 border border-green-700"
  }`
  document.body.appendChild(el)
  setTimeout(() => el.remove(), 5000)
}

function snapshotFromPixel(pixel: KlowPixel): EditSnapshot {
  const events: Record<string, string> = {
    lander: pixel.s2s_events?.lander || DEFAULT_S2S_EVENTS.lander,
    adclick: pixel.s2s_events?.adclick || DEFAULT_S2S_EVENTS.adclick,
    serp: pixel.s2s_events?.serp || DEFAULT_S2S_EVENTS.serp,
  }
  if (pixel.s2s_events?.purchase) {
    events.purchase = pixel.s2s_events.purchase
  }
  return {
    name: pixel.name,
    type: pixel.type || "TT",
    userIds: [...(pixel.user_ids ?? []).map(String)].sort(),
    pixelCode: pixel.s2s_pixel_code || "",
    events,
  }
}

function eventsEqual(a: Record<string, string>, b: Record<string, string>) {
  return JSON.stringify(a) === JSON.stringify(b)
}

function EventMappingInput({
  id,
  label,
  value,
  onChange,
  required = true,
  placeholder,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  placeholder?: string
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs text-[#b0b0b0]">
        {label}
        {!required ? <span className="text-[#888]"> (optional)</span> : null}
      </label>
      <input
        id={id}
        list={`${id}-suggestions`}
        type="text"
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-[#555] bg-[#2d2d2d] px-3 py-2 text-white"
      />
      <datalist id={`${id}-suggestions`}>
        {S2S_EVENT_SUGGESTIONS.map((eventName) => (
          <option key={eventName} value={eventName} />
        ))}
      </datalist>
    </div>
  )
}

export default function KlowPixelsTab({
  search = "",
  registerOpenCreate,
}: {
  search?: string
  registerOpenCreate?: (fn: (() => void) | null) => void
}) {
  const { user } = useAuth()
  const { data, isLoading, isError } = useKlowPixels({ userId: user?.id })
  const { data: usersData } = useAdminUsers()
  const createPixel = useCreateKlowPixel()
  const updatePixel = useUpdateKlowPixel()
  const deletePixel = useDeleteKlowPixel()

  const [modalOpen, setModalOpen] = useState(false)
  const [editPixelId, setEditPixelId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)

  const [formName, setFormName] = useState("")
  const [formType, setFormType] = useState("TT")
  const [formUserIds, setFormUserIds] = useState<string[]>([])
  const [buyerSearch, setBuyerSearch] = useState("")
  const [formPixelCode, setFormPixelCode] = useState("")
  const [formAccessToken, setFormAccessToken] = useState("")
  const [formLanderEvent, setFormLanderEvent] = useState(DEFAULT_S2S_EVENTS.lander)
  const [formAdclickEvent, setFormAdclickEvent] = useState(DEFAULT_S2S_EVENTS.adclick)
  const [formSerpEvent, setFormSerpEvent] = useState(DEFAULT_S2S_EVENTS.serp)
  const [formPurchaseEvent, setFormPurchaseEvent] = useState("")

  const [editSnapshot, setEditSnapshot] = useState<EditSnapshot | null>(null)

  const pixels: KlowPixel[] = data?.data ?? []
  const allUsers = useMemo(() => usersData?.data ?? [], [usersData])
  const filteredPixels = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return pixels
    return pixels.filter((p) => {
      const haystack = [
        p.name,
        p.label,
        p.type,
        p.s2s_pixel_code,
        p.owner_username,
        String(p.rsoc_spid),
        String(p.id),
        ...(p.owner_usernames ?? []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [pixels, search])
  const { data: detailsData } = useKlowPixelDetails(editPixelId)

  const buyerOptions = useMemo(
    () =>
      allUsers.map((u) => ({
        id: String(u.id),
        name: `${u.username} (${u.role})`,
      })),
    [allUsers],
  )

  function resetForm() {
    setEditSnapshot(null)
    setFormName("")
    setFormType("TT")
    setFormUserIds([])
    setBuyerSearch("")
    setFormPixelCode("")
    setFormAccessToken("")
    setFormLanderEvent(DEFAULT_S2S_EVENTS.lander)
    setFormAdclickEvent(DEFAULT_S2S_EVENTS.adclick)
    setFormSerpEvent(DEFAULT_S2S_EVENTS.serp)
    setFormPurchaseEvent("")
  }

  function openCreateModal() {
    setEditPixelId(null)
    resetForm()
    setModalOpen(true)
  }

  useEffect(() => {
    registerOpenCreate?.(openCreateModal)
    return () => registerOpenCreate?.(null)
  }, [registerOpenCreate])

  function openEditModal(pixel: KlowPixel) {
    setEditSnapshot(snapshotFromPixel(pixel))
    setEditPixelId(pixel.id)
    setFormName(pixel.name)
    setFormType(pixel.type || "TT")
    setFormUserIds((pixel.user_ids ?? []).map(String))
    setBuyerSearch("")
    setFormPixelCode(pixel.s2s_pixel_code || "")
    setFormAccessToken("")
    setFormLanderEvent(pixel.s2s_events?.lander || DEFAULT_S2S_EVENTS.lander)
    setFormAdclickEvent(pixel.s2s_events?.adclick || DEFAULT_S2S_EVENTS.adclick)
    setFormSerpEvent(pixel.s2s_events?.serp || DEFAULT_S2S_EVENTS.serp)
    setFormPurchaseEvent(pixel.s2s_events?.purchase || "")
    setModalOpen(true)
  }

  // Refresh the form from full pixel details whenever the edited pixel or its
  // details response changes (adjusted during render rather than in an effect).
  const [syncedDetails, setSyncedDetails] = useState({ editPixelId, detailsData })
  if (
    syncedDetails.editPixelId !== editPixelId ||
    syncedDetails.detailsData !== detailsData
  ) {
    setSyncedDetails({ editPixelId, detailsData })
    if (editPixelId && detailsData?.data) {
      const pixel = detailsData.data
      setEditSnapshot(snapshotFromPixel(pixel))
      setFormPixelCode(pixel.s2s_pixel_code || "")
      setFormUserIds((pixel.user_ids ?? []).map(String))
      setFormLanderEvent(pixel.s2s_events?.lander || DEFAULT_S2S_EVENTS.lander)
      setFormAdclickEvent(pixel.s2s_events?.adclick || DEFAULT_S2S_EVENTS.adclick)
      setFormSerpEvent(pixel.s2s_events?.serp || DEFAULT_S2S_EVENTS.serp)
      setFormPurchaseEvent(pixel.s2s_events?.purchase || "")
    }
  }

  function toggleBuyer(userId: string) {
    setFormUserIds((current) =>
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId],
    )
  }

  function buildS2sEvents() {
    const events: Record<string, string> = {
      lander: formLanderEvent.trim(),
      adclick: formAdclickEvent.trim(),
      serp: formSerpEvent.trim(),
    }
    if (formPurchaseEvent.trim()) {
      events.purchase = formPurchaseEvent.trim()
    }
    return events
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    if (!formName.trim() || !formPixelCode.trim() || formUserIds.length === 0) {
      if (formUserIds.length === 0) {
        showToast("Assign at least one media buyer", "error")
      }
      return
    }
    if (!editPixelId && !formAccessToken.trim()) {
      showToast("Access token is required for new pixels", "error")
      return
    }

    setSaving(true)

    try {
      if (editPixelId) {
        const snapshot = editSnapshot
        const body: Record<string, unknown> = {}
        const userIds = [...formUserIds].map(String).sort()
        const events = buildS2sEvents()

        if (
          !snapshot ||
          formName.trim() !== snapshot.name ||
          formType !== snapshot.type ||
          JSON.stringify(userIds) !== JSON.stringify(snapshot.userIds)
        ) {
          body.name = formName.trim()
          body.user_ids = formUserIds.map(Number)
          body.type = formType
        }

        const rsocChanged =
          !snapshot ||
          formPixelCode.trim() !== snapshot.pixelCode ||
          formAccessToken.trim() !== "" ||
          !eventsEqual(events, snapshot.events)

        if (rsocChanged) {
          body.s2s_pixel_code = formPixelCode.trim()
          body.s2s_events = events
          if (formAccessToken.trim()) {
            body.s2s_access_token = formAccessToken.trim()
          }
        }

        if (Object.keys(body).length === 0) {
          showToast("No changes to save", "error")
          setSaving(false)
          return
        }

        await updatePixel.mutateAsync({ id: editPixelId, body })
        showToast("KLOW pixel updated successfully")
      } else {
        const body: Record<string, unknown> = {
          name: formName.trim(),
          user_ids: formUserIds.map(Number),
          type: formType,
          s2s_pixel_code: formPixelCode.trim(),
          s2s_events: buildS2sEvents(),
          s2s_access_token: formAccessToken.trim(),
        }
        await createPixel.mutateAsync(body)
        showToast("KLOW pixel created successfully")
      }
      setModalOpen(false)
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Error saving pixel", "error")
    } finally {
      setSaving(false)
    }
  }

  function handleDelete(pixel: KlowPixel) {
    if (
      !window.confirm(
        `Delete KLOW pixel '${pixel.name} (${pixel.rsoc_spid})'? This removes it from RSOC too.`,
      )
    ) {
      return
    }
    deletePixel.mutate(pixel.id, {
      onSuccess: () => showToast("KLOW pixel deleted successfully"),
      onError: (err) =>
        showToast(err instanceof Error ? err.message : "Error deleting pixel", "error"),
    })
  }

  if (isLoading) {
    return <div className="py-8 text-center text-white">Loading KLOW pixels...</div>
  }

  if (isError) {
    return <div className="py-8 text-center text-red-400">Error loading KLOW pixels</div>
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4 overflow-hidden">
      <p className="shrink-0 text-sm text-[#b0b0b0]">
        Provision RSOC spid pixels and assign them to media buyers. Offer URLs use{" "}
        <code className="rounded bg-[#2d2d2d] px-1">spid=&#123;rsoc id&#125;</code>.
      </p>

      {pixels.length === 0 ? (
        <div className="py-8 text-center text-white">
          No KLOW pixels yet. Create one to assign to a media buyer.
        </div>
      ) : filteredPixels.length === 0 ? (
        <div className="py-8 text-center text-white">
          No KLOW pixels match “{search.trim()}”
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-[#4a4a4a]">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="sticky top-0 z-10 bg-[#444444] text-white">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">RSOC spid</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Pixel code</th>
                <th className="px-4 py-3 font-semibold">Assigned buyers</th>
                <th className="px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPixels.map((pixel) => (
                <tr
                  key={pixel.id}
                  className="border-b border-[#4a4a4a] transition-colors hover:bg-[#444444]"
                >
                  <td className="px-4 py-3 font-medium text-white">{pixel.name}</td>
                  <td className="px-4 py-3">
                    <code className="rounded bg-[#2d2d2d] px-1.5 py-0.5 text-xs text-white">
                      {pixel.rsoc_spid}
                    </code>
                  </td>
                  <td className="px-4 py-3 text-white">{pixel.type}</td>
                  <td className="px-4 py-3">
                    <code className="rounded bg-[#2d2d2d] px-1.5 py-0.5 text-xs text-white">
                      {pixel.s2s_pixel_code || "—"}
                    </code>
                  </td>
                  <td className="px-4 py-3 text-white">
                    {pixel.owner_username || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <button
                        className="rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#687bda]"
                        onClick={() => openEditModal(pixel)}
                      >
                        Edit
                      </button>
                      <button
                        className="rounded-md border border-[#d26a78]/35 bg-[#c6596a] px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#b84f60]"
                        onClick={() => handleDelete(pixel)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={(open) => !open && setModalOpen(false)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-[#4a4a4a] bg-[#3a3a3a] text-white">
          <DialogHeader>
            <DialogTitle>{editPixelId ? "Edit KLOW Pixel" : "Create KLOW Pixel"}</DialogTitle>
            <DialogDescription className="text-white">
              Creates the pixel on RSOC and stores assignment metadata locally.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium">Internal name</label>
              <input
                type="text"
                required
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Vlad TT Main"
                className="w-full rounded-md border border-[#555] bg-[#2d2d2d] px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Type</label>
              <select
                value={formType}
                onChange={(e) => setFormType(e.target.value)}
                className="w-full rounded-md border border-[#555] bg-[#2d2d2d] px-3 py-2 text-white"
              >
                <option value="TT">TT (TikTok)</option>
                <option value="FB">FB (Facebook)</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Assigned media buyers</label>
              <IntuitiveMultiSelect
                options={buyerOptions}
                selected={formUserIds}
                onToggle={toggleBuyer}
                search={buyerSearch}
                onSearchChange={setBuyerSearch}
                searchPlaceholder="Search buyers..."
                emptySelectionText="No buyers selected"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Pixel code (TikTok/FB id)</label>
              <input
                type="text"
                required
                value={formPixelCode}
                onChange={(e) => setFormPixelCode(e.target.value)}
                className="w-full rounded-md border border-[#555] bg-[#2d2d2d] px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">
                Access token{" "}
                {editPixelId && (
                  <span className="font-normal text-[#b0b0b0]">(leave empty to keep existing)</span>
                )}
              </label>
              <input
                type="text"
                required={!editPixelId}
                value={formAccessToken}
                onChange={(e) => setFormAccessToken(e.target.value)}
                className="w-full rounded-md border border-[#555] bg-[#2d2d2d] px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">S2S event mapping</label>
              <p className="mb-3 text-xs text-[#b0b0b0]">
                Type a custom event or pick a suggestion from the list.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <EventMappingInput
                  id="klow-event-lander"
                  label="Lander"
                  value={formLanderEvent}
                  onChange={setFormLanderEvent}
                />
                <EventMappingInput
                  id="klow-event-adclick"
                  label="Ad click"
                  value={formAdclickEvent}
                  onChange={setFormAdclickEvent}
                />
                <EventMappingInput
                  id="klow-event-serp"
                  label="SERP"
                  value={formSerpEvent}
                  onChange={setFormSerpEvent}
                />
                <EventMappingInput
                  id="klow-event-purchase"
                  label="Purchase"
                  value={formPurchaseEvent}
                  onChange={setFormPurchaseEvent}
                  required={false}
                  placeholder="Optional"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={saving || createPixel.isPending || updatePixel.isPending}
                className="flex-1 rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#687bda] disabled:opacity-50"
              >
                {saving ? "Saving..." : editPixelId ? "Update Pixel" : "Create Pixel"}
              </button>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="flex-1 rounded-md border border-[#7a808b]/45 bg-[#636b78] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#58606c]"
              >
                Cancel
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
